import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { buildPaMapFromLabelRows, fetchPaLabels } from '../lib/articlesApi';
import { localizePaLabel } from '../lib/localizedContent';
import {
  pathForPracticeArea,
  practiceAreaGoToKey,
} from '../lib/siteRoutes';

const PaLabelsContext = createContext(null);

export function PaLabelsProvider({ children }) {
  const { i18n } = useTranslation();
  const [labels, setLabels] = useState(/** @type {Array<{ slug: string, label: string, label_vn?: string | null, sort_order?: number }>} */ ([]));
  const [status, setStatus] = useState(/** @type {'idle' | 'loading' | 'ready'} */ ('idle'));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatus('loading');
      const { labels: rows } = await fetchPaLabels();
      if (cancelled) return;
      setLabels(rows ?? []);
      setStatus('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const paMap = useMemo(
    () => buildPaMapFromLabelRows(labels, i18n.language),
    [labels, i18n.language],
  );

  const labelFor = useCallback(
    (slug) => {
      if (!slug) return '';
      const row = labels.find((l) => l.slug === slug);
      if (row) return localizePaLabel(row, i18n.language);
      return paMap[slug] || slug;
    },
    [labels, paMap, i18n.language],
  );

  /** Nav / footer practice links — label from `pa_labels` (EN or VI). */
  const navPracticeLinks = useMemo(
    () =>
      labels
        .filter((row) => row.slug && row.label)
        .map((row) => ({
          slug: row.slug,
          pageKey: practiceAreaGoToKey(row.slug),
          href: pathForPracticeArea(row.slug),
          label: localizePaLabel(row, i18n.language),
        })),
    [labels, i18n.language],
  );

  const value = useMemo(
    () => ({
      labels,
      paMap,
      labelFor,
      navPracticeLinks,
      status,
    }),
    [labels, paMap, labelFor, navPracticeLinks, status],
  );

  return (
    <PaLabelsContext.Provider value={value}>{children}</PaLabelsContext.Provider>
  );
}

export function usePaLabels() {
  const ctx = useContext(PaLabelsContext);
  if (!ctx) {
    throw new Error('usePaLabels must be used within PaLabelsProvider');
  }
  return ctx;
}
